import React, { useEffect, useRef } from "react";
import dynamic from "next/dynamic";
import CustomLink from "@gc-npm/components.custom-link";
import TextInfo, {
  TEXT_COLOR,
  TEXT_FONT_WEIGHT,
  TEXT_SIZE,
} from "@gc-npm/components.text";
import style from "./style";

const Icon = dynamic(() => import("@gc-npm/components.icon-glyph"));

/**
 * @param {Array} props.items - Array of category items
 * @param {string} props.items[].label - Display text for the pill
 * @param {string} [props.items[].iconName] - Icon glyph name (from @gc-npm/components.icon-glyph)
 * @param {string} props.items[].href - Navigation URL
 * @param {boolean} [props.items[].selected] - Whether this pill is currently active
 * @param {object} [props.items[].taggingInfo] - Analytics tagging info
 */
const SuperCategoryRail = ({ id, items = [] }) => {
  const normalizedPath = `/${id}`;
  const selectedRef = useRef(null);

  useEffect(() => {
    if (selectedRef.current) {
      selectedRef.current.scrollIntoView({
        inline: "center",
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [id]);

  if (!items || items.length === 0) {
    return null;
  }

  const categoryPaths = new Set(items.map((item) => item.href));
  categoryPaths.add("/moviles");

  if (!categoryPaths.has(normalizedPath)) {
    return null;
  }

  return (
    <nav className="super-category-rail" aria-label="Categorías principales">
      <ul className="container super-category-rail__items" role="list">
        {items.map(({ label, iconId, href }, index) => {
          const selected = href === normalizedPath;
          return (
          <li
            key={`${label}-${index}`}
            ref={selected ? selectedRef : null}
            className={`super-category-rail__item${selected ? "--selected" : ""}`}
            onFocus={(e) =>
              e.currentTarget.scrollIntoView({
                inline: "nearest",
                block: "nearest",
                behavior: "smooth",
              })
            }
          >
            <CustomLink
              className="super-category-rail__item-link"
              href={selected ? "/moviles" : href}
              taggingInfo={{
                componentName: "super-category-rail",
                componentContent: label,
                componentVariation: selected ? "selected" : "default",
              }}
              aria-current={selected ? "page" : undefined}
            >
              {iconId && (
                <span className="super-category-rail__icon" aria-hidden="true">
                  <Icon
                    id={iconId}
                    height={16}
                    color={selected ? TEXT_COLOR.BLUE_HIGH : TEXT_COLOR.GREY_600}
                  />
                </span>
              )}
              <TextInfo
                text={label}
                size={TEXT_SIZE.TEXT_PRESET_03}
                color={selected ? TEXT_COLOR.BLUE_HIGH : TEXT_COLOR.MOVISTAR_BLACK}
                fontType={TEXT_FONT_WEIGHT.REGULAR}
                font
              />
            </CustomLink>
          </li>
          );
        })}
      </ul>
      <style jsx>{style}</style>
    </nav>
  );
};

export default SuperCategoryRail;
